import { Action } from "@packages/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listLoansQueryOptions } from "@/api/financial";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useFarmScope } from "@/hooks/useFarmScope";
import { formatMoney } from "@/utils/financial";
import {
	FinancialHeaderActions,
	HeaderActionButton,
} from "../-components/FinancialHeaderActions";
import { LoanStatusBadge } from "./-components/LoanBadges";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/loans/",
)({
	component: LoansListPage,
});

const ALL = "all";
const STATUS_OPTIONS = ["ACTIVE", "SETTLED", "CANCELED"] as const;

function LoansListPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const scope = useFarmScope();
	const { formatDateOnly } = useDateFormatter();
	const canManage = useHasPermission(orgId)(Action.MANAGE_FINANCE);

	const [status, setStatus] = useState(ALL);

	const establishmentId =
		scope.kind === "single" ? scope.establishmentId : undefined;

	const queryParams = useMemo(
		() => ({
			limit: "100",
			...(status !== ALL ? { status } : {}),
			...(establishmentId ? { establishmentId } : {}),
		}),
		[status, establishmentId],
	);

	const { data, isLoading } = useQuery(
		listLoansQueryOptions(orgId, queryParams),
	);
	const rows = data?.data ?? [];

	return (
		<div className="flex flex-col gap-4">
			{canManage && (
				<FinancialHeaderActions>
					<HeaderActionButton
						icon={Plus}
						label={t("financial.loans.new")}
						shortLabel={t("financial.loans.newShort")}
						onClick={() =>
							navigate({
								to: "/app/org/$orgId/financial/loans/new",
								params: { orgId },
							})
						}
					/>
				</FinancialHeaderActions>
			)}

			<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
				<Select value={status} onValueChange={setStatus}>
					<SelectTrigger className="sm:w-44">
						<SelectValue placeholder={t("financial.loans.filters.status")} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL}>
							{t("financial.loans.filters.all")}
						</SelectItem>
						{STATUS_OPTIONS.map((s) => (
							<SelectItem key={s} value={s}>
								{t(`financial.loans.statuses.${s}`)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className="card bg-base-100 shadow-md">
				<div className="card-body">
					{isLoading ? (
						<div className="flex justify-center py-16">
							<LoadingSpinner />
						</div>
					) : rows.length === 0 ? (
						<div className="py-12 text-center">
							<p className="font-medium">{t("financial.loans.empty.title")}</p>
							<p className="mt-1 text-sm text-base-content/60">
								{t("financial.loans.empty.description")}
							</p>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("financial.loans.columns.lender")}</TableHead>
									<TableHead className="text-right">
										{t("financial.loans.columns.principal")}
									</TableHead>
									<TableHead className="hidden md:table-cell">
										{t("financial.loans.columns.amortization")}
									</TableHead>
									<TableHead className="hidden md:table-cell text-center">
										{t("financial.loans.columns.installments")}
									</TableHead>
									<TableHead className="hidden sm:table-cell">
										{t("financial.loans.columns.firstPayment")}
									</TableHead>
									<TableHead>{t("financial.loans.columns.status")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{rows.map((loan) => (
									<TableRow
										key={loan.id}
										className="cursor-pointer"
										onClick={() =>
											navigate({
												to: "/app/org/$orgId/financial/loans/$loanId",
												params: { orgId, loanId: loan.id },
											})
										}
									>
										<TableCell>
											<span className="font-medium">{loan.lender}</span>
											{loan.description && (
												<span className="block text-xs text-base-content/60">
													{loan.description}
												</span>
											)}
										</TableCell>
										<TableCell className="text-right font-medium">
											{formatMoney(loan.principal)}
										</TableCell>
										<TableCell className="hidden md:table-cell">
											{t(`financial.loans.amortizations.${loan.amortization}`)}
										</TableCell>
										<TableCell className="hidden md:table-cell text-center">
											{loan.installmentsCount}
										</TableCell>
										<TableCell className="hidden sm:table-cell">
											{formatDateOnly(
												new Date(loan.firstPaymentDate).getTime(),
											)}
										</TableCell>
										<TableCell>
											<LoanStatusBadge status={loan.status} />
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>
			</div>
		</div>
	);
}
